// =====================================================================
// tpCustomer — Customer-churn metrics
//
// Pure functions only. Every function takes its inputs explicitly so the
// self-tests in churn-metrics-tests.js can call them with synthetic data,
// independent of the live D.* tables. The Dashboard / Details pages
// adapt window.TPCRM_DATA to these inputs once, at render time.
//
// Money is stored as integer **pence** (£0.01 units) end-to-end. There
// is no floating-point money math. Pence ÷ 100 only happens at the
// formatting layer (formatPounds).
//
// Date math: every "in/near a date X" check uses calendar-day truncation
// in UTC. ISO date strings (YYYY-MM-DD) compare lexicographically, which
// is exploited where ordering matters.
//
// All percentages on the dashboard return { value, n } so the UI can
// show "82% (n=11)" — no anonymous ratios.
// =====================================================================

(function () {
  'use strict';

  // ── Date helpers ────────────────────────────────────────────────────
  function toDate(x) {
    if (x instanceof Date) return x;
    return new Date(x);
  }
  function dayDiff(a, b) {
    return Math.round((toDate(a) - toDate(b)) / 86400000);
  }
  function isoMonth(date) {
    const d = toDate(date);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
      .toISOString().slice(0, 10);
  }
  function addMonths(date, months) {
    const d = toDate(date);
    return new Date(Date.UTC(
      d.getUTCFullYear(), d.getUTCMonth() + months, 1
    ));
  }
  // True if `date` is in [from, to] inclusive (date-only).
  function inWindow(date, from, to) {
    const d = toDate(date).toISOString().slice(0, 10);
    return d >= toDate(from).toISOString().slice(0, 10)
        && d <= toDate(to  ).toISOString().slice(0, 10);
  }

  // ── Period selector ────────────────────────────────────────────────
  // Default period: rolling 12 months ending today. presetPeriod(key, now)
  // returns { from, to, label, key }. The Details tab uses period to
  // bound "churned/renewed/upcoming in window".
  function presetPeriod(key, now) {
    const today = now ? toDate(now) : new Date();
    const to = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    function rolling(months) {
      const from = new Date(to); from.setUTCMonth(from.getUTCMonth() - months); return from;
    }
    switch (key) {
      case 'rolling-6':  return { key, from: rolling(6),  to, label: 'Rolling 6 months' };
      case 'rolling-12': return { key, from: rolling(12), to, label: 'Rolling 12 months' };
      case 'ytd': {
        const from = new Date(Date.UTC(to.getUTCFullYear(), 0, 1));
        return { key, from, to, label: 'This financial year (calendar)' };
      }
      default:           return { key: 'rolling-12', from: rolling(12), to, label: 'Rolling 12 months' };
    }
  }

  // ── Formatting ─────────────────────────────────────────────────────
  // GBP, no decimals on dashboard tiles. Negative numbers wear a minus.
  function formatPounds(pence) {
    if (pence == null) return '—';
    const pounds = Math.round(pence / 100);
    const sign = pounds < 0 ? '-' : '';
    const abs = Math.abs(pounds);
    return sign + '£' + abs.toLocaleString('en-GB');
  }
  // Compact GBP for chart axes: £24k, £1.2M.
  function formatPoundsCompact(pence) {
    if (pence == null) return '—';
    const pounds = pence / 100;
    const abs = Math.abs(pounds);
    const sign = pounds < 0 ? '-' : '';
    if (abs >= 1_000_000) return sign + '£' + (abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1) + 'M';
    if (abs >= 1_000)     return sign + '£' + Math.round(abs / 1_000) + 'k';
    return sign + '£' + Math.round(abs);
  }
  // UK date format: 25 May 2026.
  function formatDateUK(date) {
    if (!date) return '—';
    const d = toDate(date);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  function formatPct(value, digits) {
    if (value == null || isNaN(value)) return '—';
    return (value * 100).toFixed(digits == null ? 0 : digits) + '%';
  }

  // ── Current-state tiles ────────────────────────────────────────────

  // accounts: array of churn_qualitative rows (one per customer account).
  // Active = status === 'active'. At-risk and churned are NOT counted as
  // active logos. n is the size of the active set.
  function activeLogos(accounts) {
    const active = accounts.filter(a => a.status === 'active');
    return { value: active.length, n: active.length, accounts: active };
  }

  function liveArr(accounts) {
    const active = accounts.filter(a => a.status === 'active');
    const pence = active.reduce((s, a) => s + (a.arrPence || 0), 0);
    return { value: pence, n: active.length, accounts: active };
  }

  // Accounts whose renewalDate falls in [now, now+90d] AND who are not
  // already churned. ARR is summed; "n" is the number of accounts.
  function arrUpForRenewal(accounts, now, days) {
    const ndays = days == null ? 90 : days;
    const today = now ? toDate(now) : new Date();
    const horizon = new Date(today); horizon.setUTCDate(horizon.getUTCDate() + ndays);
    const cohort = accounts.filter(a => {
      if (!a.renewalDate || a.status === 'churned') return false;
      return inWindow(a.renewalDate, today, horizon);
    });
    const pence = cohort.reduce((s, a) => s + (a.arrPence || 0), 0);
    return { value: pence, n: cohort.length, accounts: cohort, days: ndays };
  }

  // Of the renewal pool, the ARR sitting on at-risk accounts. This is
  // the hero number on the dashboard.
  function atRiskArr(accounts, now, days) {
    const pool = arrUpForRenewal(accounts, now, days).accounts;
    const cohort = pool.filter(a => a.status === 'at_risk');
    const pence = cohort.reduce((s, a) => s + (a.arrPence || 0), 0);
    return { value: pence, n: cohort.length, accounts: cohort, pool: pool.length };
  }

  // Logos that churned during the period (count of renewal_events with
  // outcome=churned whose renewalDate is in window).
  function logosChurnedInPeriod(renewalEvents, period) {
    const cohort = renewalEvents.filter(e =>
      e.outcome === 'churned' && inWindow(e.renewalDate, period.from, period.to));
    return { value: cohort.length, n: cohort.length, events: cohort };
  }

  // £ lost in the period — sum of |arrChangePence| for churn + downgrade.
  // The values are stored negative for both, so the helper folds them by
  // |.| to express "ARR lost".
  function arrChurnedInPeriod(renewalEvents, period) {
    const cohort = renewalEvents.filter(e =>
      (e.outcome === 'churned' || e.outcome === 'downgraded')
      && inWindow(e.renewalDate, period.from, period.to));
    const pence = cohort.reduce((s, e) => s + Math.abs(e.arrChangePence || 0), 0);
    return { value: pence, n: cohort.length, events: cohort };
  }

  // Gross Revenue Retention by renewal cohort.
  //   GRR = (start ARR - downgrades - churn) / start ARR
  // Cohort = the renewal_events whose renewalDate falls in window.
  // Returns { value, n, startArr, lost }.
  function grrInPeriod(renewalEvents, period) {
    const cohort = renewalEvents.filter(e => inWindow(e.renewalDate, period.from, period.to));
    if (!cohort.length) return { value: null, n: 0, startArr: 0, lost: 0 };
    const startArr = cohort.reduce((s, e) => s + (e.startArrPence || 0), 0);
    const lost = cohort.reduce((s, e) => {
      if (e.outcome === 'churned' || e.outcome === 'downgraded') {
        return s + Math.abs(e.arrChangePence || 0);
      }
      return s;
    }, 0);
    if (!startArr) return { value: null, n: cohort.length, startArr, lost };
    return { value: (startArr - lost) / startArr, n: cohort.length, startArr, lost };
  }

  // Net Revenue Retention by renewal cohort.
  //   NRR = (start ARR + upgrades - downgrades - churn) / start ARR
  function nrrInPeriod(renewalEvents, period) {
    const cohort = renewalEvents.filter(e => inWindow(e.renewalDate, period.from, period.to));
    if (!cohort.length) return { value: null, n: 0, startArr: 0, change: 0 };
    const startArr = cohort.reduce((s, e) => s + (e.startArrPence || 0), 0);
    const change = cohort.reduce((s, e) => s + (e.arrChangePence || 0), 0);  // signed
    if (!startArr) return { value: null, n: cohort.length, startArr, change };
    return { value: (startArr + change) / startArr, n: cohort.length, startArr, change };
  }

  // ── Trend series (monthly buckets) ─────────────────────────────────

  // 12-month renewal calendar: for each of the next 12 months from
  // `from`, return { month, accounts: [{accountId, arrPence, status}],
  // arrPence, health: 'green'|'amber'|'red'|'mixed' }.
  // Health: red if any at-risk in the bucket, amber if any declining
  // (low-health-score account), green otherwise. Mixed is reserved for
  // future use.
  function renewalCalendar(accounts, from, months) {
    const m = months == null ? 12 : months;
    const start = isoMonth(from);
    const buckets = [];
    for (let i = 0; i < m; i++) {
      const monthIso = isoMonth(addMonths(start, i));
      buckets.push({ month: monthIso, accounts: [], arrPence: 0, byStatus: {} });
    }
    for (const a of accounts) {
      if (!a.renewalDate || a.status === 'churned') continue;
      const monthIso = isoMonth(a.renewalDate);
      const b = buckets.find(x => x.month === monthIso);
      if (!b) continue;
      b.accounts.push({ accountId: a.accountId, arrPence: a.arrPence || 0, status: a.status, name: a.accountName });
      b.arrPence += (a.arrPence || 0);
      b.byStatus[a.status] = (b.byStatus[a.status] || 0) + 1;
    }
    for (const b of buckets) {
      const atRisk = b.byStatus['at_risk'] || 0;
      if (b.accounts.length === 0)      b.health = 'empty';
      else if (atRisk > 0)              b.health = 'red';
      else if (b.accounts.length > 0)   b.health = 'green';
      else                              b.health = 'empty';
    }
    return buckets;
  }

  // ARR-bridge series over trailing 12 months. For each month bucket
  // (anchored at the renewalDate of each event), produces:
  //   { month, new, upgrade, downgrade, churn, net }
  // All pence, signed where appropriate. The bridge is consumed by the
  // dashboard for both the bar chart and the running-total tooltip.
  // newArrPence is the start ARR of an account that has its first
  // renewal_event in the period (new logos enter via won opportunities,
  // not via renewal events, so this is set to 0 here unless caller
  // provides newLogos).
  function arrBridge(renewalEvents, period, newLogos) {
    const from = isoMonth(period.from);
    const months = 12;
    const buckets = [];
    for (let i = 0; i < months; i++) {
      const monthIso = isoMonth(addMonths(from, i));
      buckets.push({ month: monthIso, newArr: 0, upgrade: 0, downgrade: 0, churn: 0, net: 0 });
    }
    for (const e of renewalEvents) {
      if (!inWindow(e.renewalDate, period.from, period.to)) continue;
      const monthIso = isoMonth(e.renewalDate);
      const b = buckets.find(x => x.month === monthIso);
      if (!b) continue;
      const change = e.arrChangePence || 0;
      if (e.outcome === 'upgraded')      b.upgrade   += change;
      else if (e.outcome === 'downgraded') b.downgrade += change;
      else if (e.outcome === 'churned')    b.churn     += change;
    }
    for (const n of (newLogos || [])) {
      if (!inWindow(n.startDate, period.from, period.to)) continue;
      const monthIso = isoMonth(n.startDate);
      const b = buckets.find(x => x.month === monthIso);
      if (!b) continue;
      b.newArr += (n.arrPence || 0);
    }
    for (const b of buckets) b.net = b.newArr + b.upgrade + b.downgrade + b.churn;
    return buckets;
  }

  // GRR / NRR by renewal cohort month. Returns one entry per month with
  // value=null when n=0 (so the chart can draw gaps honestly).
  function retentionTrend(renewalEvents, period) {
    const from = isoMonth(period.from);
    const months = 12;
    const out = [];
    for (let i = 0; i < months; i++) {
      const monthIso = isoMonth(addMonths(from, i));
      const monthEvents = renewalEvents.filter(e => isoMonth(e.renewalDate) === monthIso);
      const startArr = monthEvents.reduce((s, e) => s + (e.startArrPence || 0), 0);
      const lost   = monthEvents.reduce((s, e) =>
        (e.outcome === 'churned' || e.outcome === 'downgraded')
          ? s + Math.abs(e.arrChangePence || 0)
          : s, 0);
      const change = monthEvents.reduce((s, e) => s + (e.arrChangePence || 0), 0);
      out.push({
        month: monthIso,
        n: monthEvents.length,
        grr: startArr ? (startArr - lost) / startArr : null,
        nrr: startArr ? (startArr + change) / startArr : null,
        startArr,
      });
    }
    return out;
  }

  // Logo-count trend: active customers per month. An account is "active
  // in month M" if its contractStart <= end-of-M and it has not yet
  // churned by end-of-M. Returns [{ month, active }].
  function logoCountTrend(accounts, renewalEvents, period) {
    const from = isoMonth(period.from);
    const months = 12;
    const out = [];
    for (let i = 0; i < months; i++) {
      const monthIso = isoMonth(addMonths(from, i));
      const endOfMonth = addMonths(monthIso, 1);
      let active = 0;
      for (const a of accounts) {
        if (!a.contractStart) continue;
        if (toDate(a.contractStart) > toDate(endOfMonth)) continue;
        // Has the account churned by end-of-month?
        const churnEvent = renewalEvents.find(e =>
          e.accountId === a.accountId && e.outcome === 'churned'
          && toDate(e.renewalDate) <= toDate(endOfMonth));
        if (churnEvent) continue;
        active++;
      }
      out.push({ month: monthIso, active });
    }
    return out;
  }

  // ── At-risk auto-flag rules ────────────────────────────────────────
  // From the brief: suggest at_risk when ANY TWO of the following are
  // true in the latest snapshot.
  //   1. daysSinceLastLogin > 21
  //   2. activeSeats / licensedSeats < 0.4
  //   3. logins30d down >50% vs prior month
  //   4. healthScore < 50
  // Returns { suggested: boolean, ruleResults: [{key,label,tripped,detail}] }.
  // ruleResults is exposed verbatim so the drawer can show WHICH rules
  // tripped, not just the verdict.
  function evaluateAtRiskRules(latest, prior) {
    if (!latest) {
      return { suggested: false, tripCount: 0, ruleResults: [] };
    }
    const rules = [];

    rules.push({
      key: 'dormant',
      label: 'Dormant',
      tripped: latest.daysSinceLastLogin != null && latest.daysSinceLastLogin > 21,
      detail: latest.daysSinceLastLogin != null
        ? `Last login ${latest.daysSinceLastLogin}d ago (>21d trips)`
        : 'No login data',
    });

    const licensed = latest.licensedSeats || 0;
    const seatRatio = licensed > 0 ? (latest.activeSeats || 0) / licensed : null;
    rules.push({
      key: 'seats',
      label: 'Low seat usage',
      tripped: seatRatio != null && seatRatio < 0.4,
      detail: seatRatio != null
        ? `${latest.activeSeats || 0}/${licensed} seats active (${Math.round(seatRatio * 100)}%, <40% trips)`
        : 'No seat data',
    });

    let loginDropRatio = null;
    if (prior && prior.logins30d != null && prior.logins30d > 0) {
      loginDropRatio = (latest.logins30d || 0) / prior.logins30d;
    }
    rules.push({
      key: 'logins',
      label: 'Login drop',
      tripped: loginDropRatio != null && loginDropRatio < 0.5,
      detail: loginDropRatio != null
        ? `Logins ${latest.logins30d || 0} vs ${prior.logins30d} prior (${Math.round((1 - loginDropRatio) * 100)}% drop, >50% trips)`
        : 'Need prior month for comparison',
    });

    rules.push({
      key: 'health',
      label: 'Low health score',
      tripped: latest.healthScore != null && latest.healthScore < 50,
      detail: latest.healthScore != null
        ? `Health ${latest.healthScore}/100 (<50 trips)`
        : 'No health score',
    });

    const tripCount = rules.filter(r => r.tripped).length;
    return { suggested: tripCount >= 2, tripCount, ruleResults: rules };
  }

  // ── Utility: pick latest + prior snapshot for an account ──────────
  function latestAndPrior(snapshots, accountId) {
    const rows = snapshots
      .filter(s => s.accountId === accountId)
      .slice()
      .sort((a, b) => a.snapshotMonth.localeCompare(b.snapshotMonth));
    if (!rows.length) return { latest: null, prior: null, all: [] };
    return {
      latest: rows[rows.length - 1],
      prior:  rows.length >= 2 ? rows[rows.length - 2] : null,
      all:    rows,
    };
  }

  // ── Days-to-renewal + tenure ───────────────────────────────────────
  function daysToRenewal(account, now) {
    if (!account || !account.renewalDate) return null;
    return dayDiff(account.renewalDate, now || new Date());
  }
  function tenureMonths(account, now) {
    if (!account || !account.contractStart) return null;
    const days = dayDiff(now || new Date(), account.contractStart);
    return Math.floor(days / 30);
  }

  // ── Expose ────────────────────────────────────────────────────────
  window.tpChurn = {
    // formatting
    formatPounds, formatPoundsCompact, formatDateUK, formatPct,
    // periods
    presetPeriod,
    // current-state tiles
    activeLogos, liveArr,
    arrUpForRenewal, atRiskArr,
    logosChurnedInPeriod, arrChurnedInPeriod,
    grrInPeriod, nrrInPeriod,
    // trends
    renewalCalendar, arrBridge, retentionTrend, logoCountTrend,
    // at-risk
    evaluateAtRiskRules, latestAndPrior,
    // per-row utilities
    daysToRenewal, tenureMonths,
    // exposed for unit tests
    _internals: { isoMonth, addMonths, inWindow, dayDiff },
  };
}());
